import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getCookie } from 'utils/cookie.helper';
import { TApi } from 'utils/api';
import { roomsAtom } from '../store/rooms';
import { isProjectsSavedMap, TIsProjectsSavedMap } from '../store/projects';
import { ProjectStatuses, TSearchConfigurationsResponse } from '../types';
import { getSearchConfigurationsBody } from '../helpers/get-search-configurations-body';
import { parseSearchConfigurationsResponse } from '../helpers/parse-search-configurations-response';
import { clearRoomsStorage } from '../helpers/clear-rooms-storage';
import { CUSTOMER_ID_COOKIE_NAME, UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME } from '../consts/cookies';
import { ENDPOINTS } from '../consts/endpoints';
import {
  SEARCH_CONFIGURATIONS_DEFAULT_PAGE,
  SEARCH_CONFIGURATIONS_DEFAULT_SIZE,
} from '../consts/pagination-params';

type TParams = {
  apiMethods: TApi;
  isFloor: boolean;
  calculatorId?: string;
};

export const useSearchRooms = ({ apiMethods, isFloor, calculatorId }: TParams) => {
  const calculatedRooms = useRecoilValue(roomsAtom);
  const setRoomsState = useSetRecoilState(roomsAtom);
  const setSavedProjectMap = useSetRecoilState(isProjectsSavedMap);

  useEffect(() => {
    clearRoomsStorage();
    const authorizedCustomerId = getCookie(CUSTOMER_ID_COOKIE_NAME);
    const unauthorizedCustomerId = getCookie(UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME);
    const isAnyCustomerIdExists = Boolean(authorizedCustomerId) || Boolean(unauthorizedCustomerId);
    async function getRooms() {
      const body = getSearchConfigurationsBody(authorizedCustomerId, unauthorizedCustomerId);
      try {
        const configurations: TSearchConfigurationsResponse = await apiMethods.post(
          `${ENDPOINTS.SEARCH_CONFIGURATIONS}?page=${SEARCH_CONFIGURATIONS_DEFAULT_PAGE}&size=${SEARCH_CONFIGURATIONS_DEFAULT_SIZE}`,
          body
        );
        const rooms = parseSearchConfigurationsResponse(configurations, calculatorId);
        const savedProjectsMap = configurations.content
          .flatMap(el => el.configurations)
          .reduce((acc, { id, projectStatus }) => {
            if (id && projectStatus) {
              acc[id] = projectStatus === ProjectStatuses.SAVED;
            }

            return acc;
          }, {} as TIsProjectsSavedMap);

        setRoomsState(rooms);
        setSavedProjectMap(savedProjectsMap);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('get rooms failed', e);
      }
    }

    if (isFloor && calculatedRooms.length === 0 && isAnyCustomerIdExists) {
      getRooms();
    }
  }, [apiMethods, calculatedRooms.length, calculatorId, isFloor, setRoomsState]);
};
